import { useCallback, useMemo } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { ColumnDef, SortingState, VisibilityState } from '@/components/UI/dataTableConfig';
import { FaColumns } from 'react-icons/fa';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { Button, Card, DataTable, Dropdown } from '@/components/UI';
import { buildCompareColumns } from './compareColumns';
import { COLUMN_LABELS, COLUMN_PRESETS, HIDEABLE_COLUMN_IDS, PAGE_SIZE, type HideableColumnId } from './constants';
import { hiddenColumnsToVisibility, keyForRow } from './helpers';
import type { CompareFilterOptions, CompareMode, CompareRow, CompareSortBy, UpdateCompareParams } from './types';

interface PerformanceTableCardProps {
    compareFilters: CompareFilterOptions | undefined;
    compareFiltersLoading: boolean;
    compareMode: CompareMode;
    currentPage: number;
    defaultSortBy: CompareSortBy;
    error: string | null;
    hasMore: boolean;
    hiddenColumns: HideableColumnId[];
    navigate: NavigateFunction;
    pinnedRows: CompareRow[];
    rows: CompareRow[];
    rowsLoading: boolean;
    selectedStrategyId: number | null;
    selectedStrategyQuery: string;
    sortBy: CompareSortBy;
    sortDirection: 'asc' | 'desc';
    togglePinnedRow: (row: CompareRow) => void;
    totalRows: number;
    updateParams: UpdateCompareParams;
}

const PerformanceTableCard = ({
    compareFilters,
    compareFiltersLoading,
    compareMode,
    currentPage,
    defaultSortBy,
    error,
    hasMore,
    hiddenColumns,
    navigate,
    pinnedRows,
    rows,
    rowsLoading,
    selectedStrategyId,
    selectedStrategyQuery,
    sortBy,
    sortDirection,
    togglePinnedRow,
    totalRows,
    updateParams
}: PerformanceTableCardProps) => {
    const columnVisibility = useMemo<VisibilityState>(() => hiddenColumnsToVisibility(hiddenColumns), [hiddenColumns]);

    const pinnedRowKeys = useMemo(() => new Set(pinnedRows.map((row) => keyForRow(row))), [pinnedRows]);

    const sorting = useMemo<SortingState>(() => [{ id: sortBy, desc: sortDirection === 'desc' }], [sortBy, sortDirection]);

    const handleSortingChange = useCallback(
        (nextSorting: SortingState) => {
            const nextSort = nextSorting[0];

            if (!nextSort?.id) {
                updateParams({ sortBy: defaultSortBy, sortDirection: 'desc' });
                return;
            }

            updateParams({
                sortBy: String(nextSort.id),
                sortDirection: nextSort.desc ? 'desc' : 'asc'
            });
        },
        [defaultSortBy, updateParams]
    );

    const showOptionColumns = useMemo(() => rows.some((row) => (row.metrics.optionTotalEntries ?? 0) > 0), [rows]);

    const heatmapScales = useMemo(() => {
        const maxAbs = (values: (number | null | undefined)[]): number => {
            let max = 0;

            for (const value of values) {
                if (value === null || value === undefined) continue;
                const abs = Math.abs(value);
                if (abs > max) max = abs;
            }

            return max || 1;
        };

        return {
            avgProfit: maxAbs(rows.map((row) => row.metrics.avgProfitPercent)),
            medianProfit: maxAbs(rows.map((row) => row.metrics.medianProfitPercent)),
            alpha: maxAbs(rows.map((row) => row.metrics.avgAlphaPercent)),
            beatSpy: maxAbs(rows.map((row) => row.metrics.winRateVsSpy)),
            optionAvg: maxAbs(rows.map((row) => row.metrics.optionAvgProfitPercent)),
            optionMedian: maxAbs(rows.map((row) => row.metrics.medianOptionProfitPercent))
        };
    }, [rows]);

    const columns = useMemo<ColumnDef<CompareRow>[]>(
        () =>
            buildCompareColumns({
                heatmapScales,
                navigate,
                pinnedRowKeys,
                selectedStrategyId,
                selectedStrategyQuery,
                showOptionColumns,
                togglePinnedRow
            }),
        [heatmapScales, navigate, pinnedRowKeys, selectedStrategyId, selectedStrategyQuery, showOptionColumns, togglePinnedRow]
    );

    const visibleColumnCount = useMemo(
        () => columns.filter((column) => column.id && columnVisibility[column.id] !== false).length,
        [columns, columnVisibility]
    );

    const handleColumnVisibilityChange = useCallback(
        (next: VisibilityState) => {
            const nextHidden: HideableColumnId[] = [];

            for (const columnId of HIDEABLE_COLUMN_IDS) {
                if (next[columnId] === false) {
                    nextHidden.push(columnId);
                }
            }

            updateParams({ hiddenCols: nextHidden.length > 0 ? nextHidden.join(',') : null }, { resetPage: false });
        },
        [updateParams]
    );

    const applyColumnPreset = useCallback(
        (presetId: string) => {
            const preset = COLUMN_PRESETS.find((option) => option.id === presetId);
            if (!preset) return;

            const visibleSet = new Set<HideableColumnId>(preset.visible);
            const nextHidden = HIDEABLE_COLUMN_IDS.filter((columnId) => !visibleSet.has(columnId));
            updateParams({ hiddenCols: nextHidden.length > 0 ? nextHidden.join(',') : null }, { resetPage: false });
        },
        [updateParams]
    );

    const rowStart = totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rowEnd = totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + rows.length;
    const totalPages = totalRows === 0 ? 1 : Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

    const activePresetId = useMemo(() => {
        for (const preset of COLUMN_PRESETS) {
            const visibleSet = new Set<HideableColumnId>(preset.visible);
            const presetHidden = HIDEABLE_COLUMN_IDS.filter((columnId) => !visibleSet.has(columnId));

            const same = presetHidden.length === hiddenColumns.length && presetHidden.every((columnId) => hiddenColumns.includes(columnId));

            if (same) return preset.id;
        }

        return null;
    }, [hiddenColumns]);

    return compareFiltersLoading && !compareFilters ? (
        <DataLoadingSpinner text="Loading comparison filters..." />
    ) : compareFilters?.totalCompletedExperiments === 0 ? (
        <Card responsivePadding className="bg-zinc-900">
            <div className="py-10 text-center">
                <div className="text-lg font-medium text-white">No completed experiments yet.</div>
                <div className="mt-2 text-sm text-zinc-500">
                    Run and complete at least one experiment in the current scope to populate the compare table.
                </div>
            </div>
        </Card>
    ) : (
        <Card
            responsivePadding
            className="bg-zinc-900"
            headerActions={
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <div className="hidden items-center gap-1 sm:flex">
                        {COLUMN_PRESETS.map((preset) => (
                            <button
                                className={`rounded border px-2 py-1 text-xs transition-colors ${
                                    activePresetId === preset.id
                                        ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                        : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:text-white'
                                }`}
                                key={preset.id}
                                type="button"
                                onClick={() => applyColumnPreset(preset.id)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <Dropdown
                        buttonClassName="border border-zinc-700 bg-zinc-900 text-zinc-300"
                        buttonContent={
                            <span className="flex items-center gap-2 text-xs">
                                <FaColumns />
                                Columns ({visibleColumnCount})
                            </span>
                        }
                        menuClassName="w-64 max-h-80 overflow-y-auto"
                        position="right"
                    >
                        <div className="border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wide text-zinc-500">
                            Toggle columns
                        </div>
                        {HIDEABLE_COLUMN_IDS.map((columnId) => {
                            const isVisible = columnVisibility[columnId] !== false;

                            return (
                                <label
                                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800"
                                    key={columnId}
                                >
                                    <input
                                        checked={isVisible}
                                        className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                                        type="checkbox"
                                        onChange={(event) =>
                                            handleColumnVisibilityChange({
                                                ...columnVisibility,
                                                [columnId]: event.target.checked
                                            })
                                        }
                                    />
                                    {COLUMN_LABELS[columnId]}
                                </label>
                            );
                        })}
                    </Dropdown>
                    <span className="text-xs text-zinc-500">
                        {rowStart}-{rowEnd} of {totalRows.toLocaleString()}
                    </span>
                </div>
            }
            subtitle={
                compareMode === 'fixed'
                    ? 'Rows show all duration and horizon combinations unless you narrow them with the optional filters.'
                    : 'Rows use each duration’s representative horizon for faster broad comparison.'
            }
            title="Performance Table"
        >
            {error && <div className="mb-4 rounded border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>}

            {rows.some((row) => row.includeOptions) && (
                <p className="mb-4 text-sm text-zinc-400">
                    Option metrics use completed trades under the current execution policy within the experiment window. Older option labels
                    and incomplete trades are excluded. Missing option metrics do not mean zero profit. Stock and crypto metrics remain raw
                    observations; option-versus-stock comparisons need matching execution windows.
                </p>
            )}

            {rowsLoading && rows.length === 0 ? (
                <DataLoadingSpinner text="Loading comparison results..." />
            ) : rows.length === 0 ? (
                <div className="py-10 text-center">
                    <div className="text-lg font-medium text-white">No rows match the current filters.</div>
                    <div className="mt-2 text-sm text-zinc-500">Try relaxing minimum entries or switching to representative mode.</div>
                </div>
            ) : (
                <>
                    <DataTable
                        allowOverflow
                        manualSorting
                        striped
                        columnVisibility={columnVisibility}
                        columns={columns}
                        data={rows}
                        pagination={{ enabled: false, pageSize: PAGE_SIZE }}
                        search={false}
                        sorting={sorting}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        onSortingChange={handleSortingChange}
                    />

                    <div className="mt-4 flex flex-col gap-3 border-t border-zinc-800 pt-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-zinc-500">
                            Sort by clicking table headers. Pin rows you want to compare — they stay visible while you scroll.
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                disabled={currentPage <= 1}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => updateParams({ page: String(currentPage - 1) }, { resetPage: false })}
                            >
                                Previous
                            </Button>
                            <div className="min-w-[96px] text-center text-sm text-zinc-300">
                                Page {currentPage} of {totalPages}
                            </div>
                            <Button
                                disabled={!hasMore}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => updateParams({ page: String(currentPage + 1) }, { resetPage: false })}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Card>
    );
};

export default PerformanceTableCard;
